import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { ConfirmDialog } from "../components/ConfirmDialog";
import {
  listBrokerLinks,
  createBrokerLink,
  disconnectBrokerLink,
  pauseBrokerLink,
  resumeBrokerLink,
  initiateSchwabOAuth,
  getLinkStatusDisplay,
  getBrokerDisplayName,
  SUPPORTED_BROKERS,
  type BrokerLinkSummary,
  type BrokerConfig,
} from "../services/brokerMirrorService";
import { getBrokerageAccount, type BrokerageAccount } from "../services/brokerageService";

export function MirrorLinksPage() {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();

  // Account info
  const [account, setAccount] = useState<BrokerageAccount | null>(null);

  // Links
  const [links, setLinks] = useState<BrokerLinkSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add broker modal
  const [showBrokerPicker, setShowBrokerPicker] = useState(false);
  const [selectedBroker, setSelectedBroker] = useState<BrokerConfig | null>(null);
  const [showCredentialsForm, setShowCredentialsForm] = useState(false);

  // Credentials form state
  const [formFields, setFormFields] = useState<Record<string, string>>({});
  const [formAccountNumber, setFormAccountNumber] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  // OAuth redirect state
  const [oauthLoading, setOauthLoading] = useState(false);

  // Disconnect confirmation
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [linkToDisconnect, setLinkToDisconnect] = useState<BrokerLinkSummary | null>(null);
  const [disconnectLoading, setDisconnectLoading] = useState(false);

  // Action loading states (pause/resume)
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!accountId) return;
    try {
      setLoading(true);
      const [acct, allLinks] = await Promise.all([
        getBrokerageAccount(accountId),
        listBrokerLinks(),
      ]);
      setAccount(acct);
      // Filter links for this specific brokerage account
      setLinks(allLinks.filter((l) => l.brokerage_account_id === accountId));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Check for OAuth callback success/error in URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthStatus = params.get("oauth");
    const oauthError = params.get("error");

    if (oauthStatus === "success") {
      // OAuth succeeded — reload to show the now-active link
      loadData();
      // Clean up URL
      window.history.replaceState({}, "", window.location.pathname);
    } else if (oauthError) {
      setError(`OAuth failed: ${oauthError}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [loadData]);

  // ---- Handlers ----

  function handleSelectBroker(broker: BrokerConfig) {
    if (!broker.implemented) return;
    setSelectedBroker(broker);
    setShowBrokerPicker(false);
    setShowCredentialsForm(true);
    setFormFields({});
    setFormAccountNumber("");
    setFormError(null);
  }

  function handleFieldChange(fieldName: string, value: string) {
    setFormFields((prev) => ({ ...prev, [fieldName]: value }));
  }

  async function handleCreateAndOAuth(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedBroker || !accountId) return;

    // Validate required fields
    for (const field of selectedBroker.fields) {
      if (!formFields[field.name]?.trim()) {
        setFormError(`${field.label} is required`);
        return;
      }
    }

    try {
      setFormLoading(true);
      setFormError(null);

      // Step 1: Create the broker link with credentials
      const link = await createBrokerLink({
        brokerage_account_id: accountId,
        broker_type: selectedBroker.type,
        broker_account_number: formAccountNumber || undefined,
        app_key: formFields["app_key"] || "",
        app_secret: formFields["app_secret"] || "",
      });

      // Step 2: If OAuth flow, initiate redirect
      if (selectedBroker.authFlow === "oauth") {
        setOauthLoading(true);
        const oauthResponse = await initiateSchwabOAuth(link.id);

        // Redirect browser to Schwab's authorization page
        window.location.href = oauthResponse.authorization_url;
        return; // Page will navigate away
      }

      // Non-OAuth brokers: just reload
      setShowCredentialsForm(false);
      setSelectedBroker(null);
      await loadData();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Failed to create broker link");
      setOauthLoading(false);
    } finally {
      setFormLoading(false);
    }
  }

  async function handlePauseResume(link: BrokerLinkSummary) {
    setActionLoading(link.id);
    try {
      if (link.mirror_enabled && link.status !== "paused") {
        await pauseBrokerLink(link.id);
      } else {
        await resumeBrokerLink(link.id);
      }
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Action failed");
    } finally {
      setActionLoading(null);
    }
  }

  function handleDisconnectClick(link: BrokerLinkSummary) {
    setLinkToDisconnect(link);
    setShowDisconnectModal(true);
  }

  async function handleConfirmDisconnect() {
    if (!linkToDisconnect) return;
    setDisconnectLoading(true);
    try {
      await disconnectBrokerLink(linkToDisconnect.id);
      setShowDisconnectModal(false);
      setLinkToDisconnect(null);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to disconnect");
    } finally {
      setDisconnectLoading(false);
    }
  }

  // ---- Rendering helpers ----

  function formatRelativeTime(dateStr: string | undefined): string {
    if (!dateStr) return "Never";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  }

  return (
    <div>
      <PageHeader
        title="Trade Mirroring"
        subtitle={account ? `Mirror trades from "${account.account_name}" to external brokers` : "Mirror your Alpaca trades to external brokers"}
        backgroundImage="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1920&q=80"
      />

      <div className="px-4 sm:px-6 lg:px-8 pb-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-surface-400 mb-6">
          <Link to="/brokerage" className="hover:text-primary-400 transition-colors">Brokerage</Link>
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          <span className="text-surface-200">{account?.account_name || "Account"}</span>
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          <span className="text-surface-200">Mirror</span>
        </nav>

        {error && (
          <div className="mb-6 glass-card bg-red-500/10 border-red-500/30 text-red-400 px-4 py-3">
            <div className="flex items-start gap-2">
              <svg className="h-5 w-5 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              <div>
                <p className="text-sm">{error}</p>
                <button onClick={() => setError(null)} className="text-xs text-red-300 hover:text-red-200 mt-1 transition-colors">Dismiss</button>
              </div>
            </div>
          </div>
        )}

        {/* Info banner */}
        <div className="mb-6 glass-card bg-surface-800/50 border-surface-700/50 px-4 py-3">
          <div className="flex items-start gap-3">
            <svg className="h-5 w-5 text-primary-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-sm text-surface-300">
              <p>
                Link an external brokerage account to automatically mirror your Alpaca trades.
                When TradeCraft fills an order on Alpaca, the same trade will be placed on your linked broker.
              </p>
              <p className="text-surface-400 mt-1">
                Each broker requires its own credentials and authorization. Your credentials are encrypted at rest.
              </p>
            </div>
          </div>
        </div>

        {/* Header + Add button */}
        <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <p className="text-surface-400">
            {links.length === 0
              ? "No external brokers linked yet."
              : `${links.length} broker${links.length !== 1 ? "s" : ""} linked`}
          </p>
          <button
            onClick={() => setShowBrokerPicker(true)}
            className="btn-primary inline-flex items-center self-start sm:self-auto"
          >
            <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Link External Broker
          </button>
        </div>

        {/* Links list */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <div className="spinner w-8 h-8" />
              <p className="text-surface-400">Loading mirror links...</p>
            </div>
          </div>
        ) : links.length === 0 ? (
          <div className="glass-card p-6 sm:p-12 text-center">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-surface-800/50 flex items-center justify-center mb-6">
              <svg className="w-10 h-10 text-surface-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-surface-100 mb-2">No brokers linked</h3>
            <p className="text-surface-400 mb-6 max-w-md mx-auto">
              Link an external broker like Charles Schwab to mirror your Alpaca paper trades to your real brokerage account.
            </p>
            <button
              onClick={() => setShowBrokerPicker(true)}
              className="btn-primary"
            >
              Link External Broker
            </button>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {links.map((link) => {
              const statusInfo = getLinkStatusDisplay(link.status);
              const isPaused = link.status === "paused";
              const needsAuth = link.status === "pending" || link.status === "token_expired";
              const isActionLoading = actionLoading === link.id;

              return (
                <div key={link.id} className="glass-card overflow-hidden">
                  <div className="p-5">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-semibold text-surface-100">
                        {getBrokerDisplayName(link.broker_type)}
                      </h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded ${statusInfo.colorClass}`}>
                        {statusInfo.label}
                      </span>
                    </div>

                    {/* Account number */}
                    {link.broker_account_number && (
                      <p className="text-sm text-surface-400 mb-3">
                        Account: ****{link.broker_account_number.slice(-4)}
                      </p>
                    )}

                    {/* Today's mirror stats */}
                    <div className="grid grid-cols-3 gap-2 py-2 border-t border-b border-surface-700/50 mb-3">
                      <div>
                        <p className="text-xs text-surface-500">Mirrored</p>
                        <p className="text-sm font-medium text-surface-200">{link.today_mirrored}</p>
                      </div>
                      <div>
                        <p className="text-xs text-surface-500">Completed</p>
                        <p className="text-sm font-medium text-accent-400">{link.today_completed}</p>
                      </div>
                      <div>
                        <p className="text-xs text-surface-500">Failed</p>
                        <p className="text-sm font-medium text-red-400">{link.today_failed}</p>
                      </div>
                    </div>

                    {/* Last mirror + error */}
                    <div className="space-y-1">
                      <p className="text-xs text-surface-500">
                        Last mirror: {formatRelativeTime(link.last_mirror_at)}
                      </p>
                      {link.last_mirror_error && (
                        <p className="text-xs text-red-400 truncate" title={link.last_mirror_error}>
                          Error: {link.last_mirror_error}
                        </p>
                      )}
                      <p className="text-xs text-surface-500">
                        Connected {new Date(link.created_at).toLocaleDateString()}
                      </p>
                    </div>

                    {/* Token expiry warnings */}
                    {link.status === "token_expiring" && (
                      <div className="mt-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-3 py-2">
                        <p className="text-xs text-yellow-400">
                          Token expiring soon. Re-authorize to keep mirroring active.
                        </p>
                      </div>
                    )}
                    {needsAuth && (
                      <div className="mt-3 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
                        <p className="text-xs text-red-400">
                          {link.status === "pending" ? "OAuth required to activate." : "Token expired. Re-authorize to resume mirroring."}
                        </p>
                        <button
                          onClick={async () => {
                            try {
                              setOauthLoading(true);
                              const resp = await initiateSchwabOAuth(link.id);
                              window.location.href = resp.authorization_url;
                            } catch (err) {
                              setError(err instanceof Error ? err.message : "OAuth initiation failed");
                              setOauthLoading(false);
                            }
                          }}
                          disabled={oauthLoading}
                          className="mt-2 text-xs font-medium text-primary-400 hover:text-primary-300 transition-colors"
                        >
                          {oauthLoading ? "Redirecting..." : "Authorize Now"}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="bg-surface-800/50 px-5 py-3 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 border-t border-surface-700/50">
                    <div className="flex gap-3">
                      <button
                        onClick={() => navigate(`/brokerage/${accountId}/mirror/${link.id}`)}
                        className="text-sm text-primary-400 hover:text-primary-300 transition-colors"
                      >
                        History
                      </button>
                      {link.status !== "pending" && (
                        <button
                          onClick={async () => {
                            try {
                              setOauthLoading(true);
                              const resp = await initiateSchwabOAuth(link.id);
                              window.location.href = resp.authorization_url;
                            } catch (err) {
                              setError(err instanceof Error ? err.message : "OAuth initiation failed");
                              setOauthLoading(false);
                            }
                          }}
                          disabled={oauthLoading}
                          className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
                        >
                          {oauthLoading ? "Redirecting..." : "Re-authenticate"}
                        </button>
                      )}
                    </div>
                    <div className="flex gap-3">
                      {!needsAuth && (
                        <button
                          onClick={() => handlePauseResume(link)}
                          disabled={isActionLoading}
                          className="text-sm text-surface-400 hover:text-surface-200 transition-colors disabled:opacity-50"
                        >
                          {isActionLoading ? "..." : isPaused ? "Resume" : "Pause"}
                        </button>
                      )}
                      <button
                        onClick={() => handleDisconnectClick(link)}
                        className="text-sm text-red-400 hover:text-red-300 transition-colors"
                      >
                        Disconnect
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Broker Picker Modal */}
      {showBrokerPicker && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowBrokerPicker(false)} />

            <div className="relative glass-card max-w-lg w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-surface-100">Choose a Broker</h3>
                <button
                  onClick={() => setShowBrokerPicker(false)}
                  className="text-surface-400 hover:text-surface-200 transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <p className="text-sm text-surface-400 mb-4">
                Select which external broker you want to mirror your Alpaca trades to.
              </p>

              <div className="space-y-3">
                {SUPPORTED_BROKERS.map((broker) => (
                  <button
                    key={broker.type}
                    onClick={() => handleSelectBroker(broker)}
                    disabled={!broker.implemented}
                    className={`w-full text-left p-4 rounded-lg border transition-all ${
                      broker.implemented
                        ? "border-surface-700/50 hover:border-primary-500/50 hover:bg-surface-800/50 cursor-pointer"
                        : "border-surface-700/30 opacity-60 cursor-not-allowed"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-surface-100">{broker.displayName}</p>
                        <p className="text-xs text-surface-400 mt-1">{broker.description}</p>
                      </div>
                      {!broker.implemented && (
                        <span className="flex-shrink-0 ml-3 px-2 py-1 text-xs font-medium bg-surface-700/50 text-surface-400 rounded">
                          Coming Soon
                        </span>
                      )}
                      {broker.implemented && (
                        <svg className="flex-shrink-0 ml-3 h-5 w-5 text-surface-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Credentials Form Modal */}
      {showCredentialsForm && selectedBroker && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={() => { setShowCredentialsForm(false); setSelectedBroker(null); }} />

            <div className="relative glass-card max-w-lg w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-surface-100">
                  Connect {selectedBroker.displayName}
                </h3>
                <button
                  onClick={() => { setShowCredentialsForm(false); setSelectedBroker(null); }}
                  className="text-surface-400 hover:text-surface-200 transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* OAuth note */}
              {selectedBroker.oauthCallbackNote && (
                <div className="mb-4 bg-primary-500/10 border border-primary-500/20 rounded-lg px-3 py-2">
                  <p className="text-xs text-primary-400">{selectedBroker.oauthCallbackNote}</p>
                </div>
              )}

              <form onSubmit={handleCreateAndOAuth}>
                {formError && (
                  <div className="mb-4 bg-red-500/10 border border-red-500/30 text-red-400 px-3 py-2 rounded-lg text-sm">
                    {formError}
                  </div>
                )}

                <div className="space-y-4">
                  {/* Broker Account Number (required) */}
                  <div>
                    <label className="block text-sm font-medium text-surface-300 mb-1">
                      Broker Account Number <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={formAccountNumber}
                      onChange={(e) => setFormAccountNumber(e.target.value)}
                      placeholder="XXXX-YYYY"
                      autoComplete="off"
                      className="w-full px-3 py-2.5 bg-surface-800 border border-surface-600 rounded-lg text-surface-100 placeholder-surface-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-sm"
                      required
                    />
                  </div>

                  {/* Dynamic broker-specific fields */}
                  {selectedBroker.fields.map((field) => (
                    <div key={field.name}>
                      <label className="block text-sm font-medium text-surface-300 mb-1">
                        {field.label}
                      </label>
                      <input
                        type={field.type}
                        value={formFields[field.name] || ""}
                        onChange={(e) => handleFieldChange(field.name, e.target.value)}
                        placeholder={field.placeholder}
                        autoComplete="one-time-code"
                        data-lpignore="true"
                        data-1p-ignore="true"
                        className="w-full px-3 py-2.5 bg-surface-800 border border-surface-600 rounded-lg text-surface-100 placeholder-surface-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors font-mono text-sm"
                        required
                      />
                      {field.helpText && (
                        <p className="mt-1 text-xs text-surface-500">{field.helpText}</p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Learn more link */}
                {selectedBroker.learnMoreUrl && (
                  <p className="mt-4 text-xs text-surface-500">
                    Need credentials?{" "}
                    <a
                      href={selectedBroker.learnMoreUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-400 hover:text-primary-300 transition-colors underline"
                    >
                      Visit the {selectedBroker.displayName} Developer Portal
                    </a>
                  </p>
                )}

                {/* Security note */}
                <div className="mt-4 pt-3 border-t border-surface-700/30">
                  <p className="text-xs text-surface-400">
                    <strong className="text-yellow-400">Your credentials are encrypted at rest</strong> using AES-256-GCM.
                    They are never stored in plaintext or exposed in API responses.
                  </p>
                </div>

                <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => { setShowCredentialsForm(false); setSelectedBroker(null); }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={formLoading || oauthLoading || !formAccountNumber.trim()}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {oauthLoading
                      ? "Redirecting to Schwab..."
                      : formLoading
                        ? "Connecting..."
                        : selectedBroker.authFlow === "oauth"
                          ? `Connect & Authorize with ${selectedBroker.displayName}`
                          : "Connect"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Disconnect Confirmation */}
      <ConfirmDialog
        isOpen={showDisconnectModal}
        onClose={() => { setShowDisconnectModal(false); setLinkToDisconnect(null); }}
        onConfirm={handleConfirmDisconnect}
        title="Disconnect Broker"
        message={`Are you sure you want to disconnect ${linkToDisconnect ? getBrokerDisplayName(linkToDisconnect.broker_type) : "this broker"}? All pending mirrors will be cancelled and your credentials will be removed.`}
        confirmText="Disconnect"
        variant="danger"
        isLoading={disconnectLoading}
      />
    </div>
  );
}
